import axiosClient from 'api/axiosClient';
import { useEffect, useMemo, useState } from 'react';

const statusLabel = {
  NORMAL: 'Đang hoạt động',
  REGISTERED: 'Chờ kích hoạt',
  SUSPENDED: 'Tạm khóa',
  DELETED: 'Đã xóa',
};

const roleLabel = {
  ADMIN: 'Quản trị viên',
  OPERATOR: 'Nhân viên vận hành',
  AGENCY: 'Đối tác',
  DRIVER: 'Khách hàng',
  SALE: 'CTV bán hàng',
  INVESTOR: 'Chủ đầu tư',
  LANDLORD: 'Chủ mặt bằng',
};

function AffiliateUserNode({ node }) {
  const [isOpen, setIsOpen] = useState(node.level < 1);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const displayName = node.fullname || node.email || 'Chưa cập nhật tên';
  const referrerName = node.referrer
    ? node.referrer.fullname || node.referrer.email || node.referrer.userCode || `ID ${node.referrer.userId}`
    : 'Tài khoản gốc';

  useEffect(() => {
    setIsOpen(node.level < 1);
  }, [node.userId, node.level]);

  return (
    <div className={`affiliate-tree-node level-${Math.min(node.level || 0, 5)}`}>
      <div className="affiliate-tree-node-row">
        <div className="affiliate-tree-person-cell">
          <button
            type="button"
            className={`affiliate-tree-toggle ${hasChildren ? '' : 'is-empty'}`}
            onClick={() => hasChildren && setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Thu gọn tuyến dưới' : 'Mở tuyến dưới'}
          >
            {hasChildren
              ? <i className={`fa-solid fa-chevron-${isOpen ? 'down' : 'right'}`} />
              : <span />}
          </button>

          <div className="affiliate-tree-avatar">
            {displayName.slice(0, 1).toUpperCase()}
          </div>

          <div className="affiliate-tree-info">
            <div className="affiliate-tree-title">
              <strong>{displayName}</strong>
              <span className={`affiliate-tree-status status-${(node.status || '').toLowerCase()}`}>
                {statusLabel[node.status] || node.status || 'Chưa xác định'}
              </span>
            </div>
            <div className="affiliate-tree-contact">
              <span><i className="fa-regular fa-envelope" /> {node.email || 'Chưa có email'}</span>
              <span><i className="fa-solid fa-phone" /> {node.phone || 'Chưa có SĐT'}</span>
            </div>
          </div>
        </div>

        <div className="affiliate-tree-identity-cell">
          <b>{node.userCode || `ID ${node.userId}`}</b>
          <span>{roleLabel[node.role] || node.role || 'Chưa phân vai trò'}</span>
        </div>

        <div className="affiliate-tree-referrer-cell">
          <span>Được giới thiệu bởi</span>
          <b>{referrerName}</b>
        </div>

        <div className="affiliate-tree-count">
          <b>{node.childrenCount || 0}</b>
          <span>trực tiếp</span>
          {(node.descendantsCount || 0) > (node.childrenCount || 0) && (
            <small>{node.descendantsCount} toàn mạng</small>
          )}
        </div>
      </div>

      {hasChildren && isOpen && (
        <div className="affiliate-tree-children">
          {node.children.map(child => (
            <AffiliateUserNode key={child.userId} node={child} />
          ))}
        </div>
      )}
    </div>
  );
}

function AffiliateTreeScreen() {
  const [treeData, setTreeData] = useState(null);
  const [q, setQ] = useState('');
  const [maxDepth, setMaxDepth] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const roots = treeData?.roots || [];
  const totals = useMemo(() => ({
    totalUsers: treeData?.totalUsers || 0,
    totalReferrals: treeData?.totalReferrals || 0,
    rootCount: treeData?.rootCount || 0,
  }), [treeData]);

  const referralRate = totals.totalUsers
    ? Math.round((totals.totalReferrals / totals.totalUsers) * 100)
    : 0;

  const fetchAffiliateTree = async () => {
    setIsLoading(true);
    try {
      const response = await axiosClient.get('/user/affiliate-tree', {
        params: { q: q.trim() || undefined, maxDepth },
      });
      setTreeData(response);
    } catch (error) {
      console.error('Failed to load affiliate tree', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateTree();
  }, []);

  const onSubmitSearch = (event) => {
    event.preventDefault();
    fetchAffiliateTree();
  };

  const clearSearch = () => {
    setQ('');
    setIsLoading(true);
    axiosClient.get('/user/affiliate-tree', { params: { maxDepth } })
      .then(setTreeData)
      .catch(error => console.error('Failed to load affiliate tree', error))
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="affiliate-tree-screen">
      <style>{`
        .affiliate-tree-screen { color: #161b33; }
        .affiliate-tree-card {
          background: #fff;
          border: 1px solid #e8ebf3;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(39, 50, 92, 0.04);
        }
        .affiliate-tree-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          padding: 18px 20px;
          margin-bottom: 16px;
        }
        .affiliate-tree-heading { display: flex; align-items: center; gap: 12px; }
        .affiliate-tree-heading-icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 8px;
          background: #eafaf1;
          color: #25bd6b;
          font-size: 18px;
          flex: 0 0 auto;
        }
        .affiliate-tree-header h1 {
          margin: 0 0 5px;
          font-size: 22px;
          line-height: 1.25;
          font-weight: 700;
          color: #3f4254;
        }
        .affiliate-tree-header p { margin: 0; color: #8b93a7; font-size: 14px; }
        .affiliate-tree-guide {
          display: flex;
          align-items: center;
          gap: 9px;
          color: #697188;
          font-size: 13px;
          white-space: nowrap;
        }
        .affiliate-tree-guide i { color: #25bd6b; }
        .affiliate-tree-overview {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          margin-bottom: 16px;
          overflow: hidden;
        }
        .affiliate-tree-stat {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 22px;
          border-right: 1px solid #edf0f6;
        }
        .affiliate-tree-stat:last-child { border-right: 0; }
        .affiliate-tree-stat-icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 8px;
          background: #f0f7ff;
          color: #388ef7;
          font-size: 17px;
        }
        .affiliate-tree-stat:nth-child(2) .affiliate-tree-stat-icon { background: #eafaf1; color: #25bd6b; }
        .affiliate-tree-stat:nth-child(3) .affiliate-tree-stat-icon { background: #fff6e8; color: #ef9d2f; }
        .affiliate-tree-stat-label { color: #8b93a7; font-size: 13px; font-weight: 600; }
        .affiliate-tree-stat-value { display: flex; align-items: baseline; gap: 8px; margin-top: 2px; }
        .affiliate-tree-stat-value strong { font-size: 24px; line-height: 1; }
        .affiliate-tree-stat-value small { color: #a0a7b8; font-size: 12px; }
        .affiliate-tree-panel { padding: 20px; }
        .affiliate-tree-panel-title {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 16px;
        }
        .affiliate-tree-panel-title h2 { margin: 0 0 4px; font-size: 17px; font-weight: 700; }
        .affiliate-tree-panel-title p { margin: 0; color: #949bad; font-size: 13px; }
        .affiliate-tree-result-count { color: #25bd6b; font-size: 13px; font-weight: 700; }
        .affiliate-tree-toolbar {
          display: grid;
          grid-template-columns: minmax(260px, 1fr) 170px auto;
          align-items: end;
          gap: 12px;
          padding: 15px;
          margin-bottom: 16px;
          background: #f8f9fc;
          border: 1px solid #edf0f6;
          border-radius: 8px;
        }
        .affiliate-tree-field label {
          display: block;
          margin-bottom: 7px;
          color: #50586f;
          font-size: 12px;
          font-weight: 700;
        }
        .affiliate-tree-input-wrap { position: relative; }
        .affiliate-tree-input-wrap > i {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #aeb5c5;
        }
        .affiliate-tree-input-wrap .form-control { padding-left: 40px; padding-right: 38px; }
        .affiliate-tree-clear {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border: 0;
          background: transparent;
          color: #9aa2b5;
        }
        .affiliate-tree-toolbar .form-control,
        .affiliate-tree-toolbar .form-select {
          height: 42px;
          border: 1px solid #dfe3ed;
          border-radius: 6px;
          background-color: #fff;
          font-size: 13px;
        }
        .affiliate-tree-toolbar .btn {
          height: 42px;
          min-width: 112px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 700;
        }
        .affiliate-tree-columns {
          display: grid;
          grid-template-columns: minmax(360px, 1.7fr) minmax(150px, .65fr) minmax(180px, .8fr) 96px;
          gap: 16px;
          padding: 10px 14px;
          color: #828a9f;
          background: #f8f9fc;
          border: 1px solid #e7eaf2;
          border-radius: 6px 6px 0 0;
          font-size: 12px;
          font-weight: 700;
        }
        .affiliate-tree-columns span:last-child { text-align: center; }
        .affiliate-tree-list { border: 1px solid #e7eaf2; border-top: 0; border-radius: 0 0 6px 6px; }
        .affiliate-tree-node { position: relative; }
        .affiliate-tree-node-row {
          display: grid;
          grid-template-columns: minmax(360px, 1.7fr) minmax(150px, .65fr) minmax(180px, .8fr) 96px;
          align-items: center;
          gap: 16px;
          min-height: 76px;
          padding: 11px 14px;
          background: #fff;
          border-bottom: 1px solid #edf0f6;
          transition: background-color .15s ease;
        }
        .affiliate-tree-node-row:hover { background: #fbfcfe; }
        .affiliate-tree-list > .affiliate-tree-node:last-child > .affiliate-tree-node-row { border-bottom: 0; }
        .affiliate-tree-person-cell { display: flex; align-items: center; min-width: 0; gap: 11px; }
        .affiliate-tree-toggle {
          width: 28px;
          height: 28px;
          flex: 0 0 28px;
          border: 1px solid #dce3ed;
          border-radius: 6px;
          background: #fff;
          color: #667087;
          font-size: 11px;
        }
        .affiliate-tree-toggle:not(.is-empty):hover { border-color: #25bd6b; color: #25bd6b; }
        .affiliate-tree-toggle.is-empty { border-color: transparent; background: transparent; cursor: default; }
        .affiliate-tree-avatar {
          width: 42px;
          height: 42px;
          flex: 0 0 42px;
          display: grid;
          place-items: center;
          border-radius: 7px;
          background: #eafaf1;
          color: #1fad60;
          font-size: 17px;
          font-weight: 800;
        }
        .affiliate-tree-info { min-width: 0; }
        .affiliate-tree-title { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .affiliate-tree-title strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; }
        .affiliate-tree-status {
          flex: 0 0 auto;
          padding: 3px 7px;
          border-radius: 4px;
          background: #eafaf1;
          color: #1fad60;
          font-size: 10px;
          font-weight: 700;
        }
        .affiliate-tree-status.status-registered { background: #fff4e5; color: #e58a1f; }
        .affiliate-tree-status.status-suspended,
        .affiliate-tree-status.status-deleted { background: #fff0f0; color: #e65656; }
        .affiliate-tree-contact {
          display: flex;
          gap: 13px;
          min-width: 0;
          margin-top: 5px;
          color: #8c94a7;
          font-size: 12px;
        }
        .affiliate-tree-contact span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .affiliate-tree-contact i { width: 13px; color: #b0b7c6; }
        .affiliate-tree-identity-cell,
        .affiliate-tree-referrer-cell { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .affiliate-tree-identity-cell b,
        .affiliate-tree-referrer-cell b { overflow: hidden; color: #4f576c; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
        .affiliate-tree-identity-cell span,
        .affiliate-tree-referrer-cell span { color: #9aa2b3; font-size: 11px; }
        .affiliate-tree-count { text-align: center; }
        .affiliate-tree-count b { display: block; color: #1fad60; font-size: 19px; line-height: 1; }
        .affiliate-tree-count span { display: block; margin-top: 4px; color: #939bad; font-size: 10px; }
        .affiliate-tree-count small { display: block; margin-top: 2px; color: #697188; font-size: 9px; }
        .affiliate-tree-children {
          position: relative;
          padding-left: 28px;
          background: #fbfcfe;
        }
        .affiliate-tree-children::before {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 17px;
          width: 1px;
          background: #bcefd2;
        }
        .affiliate-tree-children > .affiliate-tree-node > .affiliate-tree-node-row::before {
          content: '';
          position: absolute;
          left: -11px;
          top: 38px;
          width: 11px;
          height: 1px;
          background: #bcefd2;
        }
        .affiliate-tree-empty {
          padding: 52px 24px;
          text-align: center;
          color: #8b93a6;
        }
        .affiliate-tree-empty i { display: block; margin-bottom: 12px; color: #25bd6b; font-size: 25px; }
        .affiliate-tree-empty strong { display: block; margin-bottom: 4px; color: #525a70; font-size: 14px; }
        .affiliate-tree-empty span { font-size: 12px; }
        @media (max-width: 1200px) {
          .affiliate-tree-columns { display: none; }
          .affiliate-tree-list { border-top: 1px solid #e7eaf2; border-radius: 6px; }
          .affiliate-tree-node-row { grid-template-columns: minmax(300px, 1fr) 140px 90px; }
          .affiliate-tree-referrer-cell { display: none; }
        }
        @media (max-width: 768px) {
          .affiliate-tree-header { align-items: flex-start; padding: 18px; }
          .affiliate-tree-guide { display: none; }
          .affiliate-tree-overview { grid-template-columns: 1fr; }
          .affiliate-tree-stat { border-right: 0; border-bottom: 1px solid #edf0f6; }
          .affiliate-tree-stat:last-child { border-bottom: 0; }
          .affiliate-tree-toolbar { grid-template-columns: 1fr; }
          .affiliate-tree-panel-title { align-items: flex-start; flex-direction: column; }
          .affiliate-tree-node-row { grid-template-columns: minmax(230px, 1fr) 68px; gap: 8px; padding: 10px; }
          .affiliate-tree-identity-cell { display: none; }
          .affiliate-tree-contact { display: block; }
          .affiliate-tree-contact span { display: block; margin-top: 3px; }
          .affiliate-tree-children { padding-left: 18px; }
          .affiliate-tree-children::before { left: 9px; }
          .affiliate-tree-children > .affiliate-tree-node > .affiliate-tree-node-row::before { left: -9px; width: 9px; }
        }
      `}</style>

      <header className="affiliate-tree-header affiliate-tree-card">
        <div className="affiliate-tree-heading">
          <div className="affiliate-tree-heading-icon"><i className="fa-solid fa-sitemap" /></div>
          <div>
            <h1>Cây hệ thống Affiliate</h1>
            <p>Theo dõi quan hệ giới thiệu và toàn bộ tuyến dưới trong hệ thống.</p>
          </div>
        </div>
        <div className="affiliate-tree-guide">
          <i className="fa-solid fa-circle-info" />
          Nhấn mũi tên để mở hoặc thu gọn tuyến dưới
        </div>
      </header>

      <section className="affiliate-tree-overview affiliate-tree-card">
        <div className="affiliate-tree-stat">
          <div className="affiliate-tree-stat-icon"><i className="fa-solid fa-users" /></div>
          <div>
            <div className="affiliate-tree-stat-label">Tổng tài khoản</div>
            <div className="affiliate-tree-stat-value"><strong>{totals.totalUsers}</strong><small>tài khoản</small></div>
          </div>
        </div>
        <div className="affiliate-tree-stat">
          <div className="affiliate-tree-stat-icon"><i className="fa-solid fa-link" /></div>
          <div>
            <div className="affiliate-tree-stat-label">Đã có người giới thiệu</div>
            <div className="affiliate-tree-stat-value"><strong>{totals.totalReferrals}</strong><small>{referralRate}% hệ thống</small></div>
          </div>
        </div>
        <div className="affiliate-tree-stat">
          <div className="affiliate-tree-stat-icon"><i className="fa-solid fa-seedling" /></div>
          <div>
            <div className="affiliate-tree-stat-label">Tài khoản gốc</div>
            <div className="affiliate-tree-stat-value"><strong>{totals.rootCount}</strong><small>chưa có tuyến trên</small></div>
          </div>
        </div>
      </section>

      <section className="affiliate-tree-panel affiliate-tree-card">
        <div className="affiliate-tree-panel-title">
          <div>
            <h2>Danh sách mạng lưới</h2>
            <p>Tìm một tài khoản để truy xuất người giới thiệu và các tuyến liên quan.</p>
          </div>
          <div className="affiliate-tree-result-count">{roots.length} nhánh đang hiển thị</div>
        </div>

        <form className="affiliate-tree-toolbar" onSubmit={onSubmitSearch}>
          <div className="affiliate-tree-field">
            <label htmlFor="affiliate-tree-search">Tìm kiếm tài khoản</label>
            <div className="affiliate-tree-input-wrap">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                id="affiliate-tree-search"
                className="form-control"
                value={q}
                onChange={event => setQ(event.target.value)}
                placeholder="Tên, email, số điện thoại, mã giới thiệu hoặc ID"
              />
              {q && (
                <button type="button" className="affiliate-tree-clear" onClick={clearSearch} title="Xóa tìm kiếm">
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
          </div>
          <div className="affiliate-tree-field">
            <label htmlFor="affiliate-tree-depth">Độ sâu hiển thị</label>
            <select
              id="affiliate-tree-depth"
              className="form-select"
              value={maxDepth}
              onChange={event => setMaxDepth(Number(event.target.value))}
            >
              <option value={3}>Tối đa 3 tầng</option>
              <option value={5}>Tối đa 5 tầng</option>
              <option value={10}>Tối đa 10 tầng</option>
              <option value={20}>Tối đa 20 tầng</option>
            </select>
          </div>
          <button className="btn btn-primary" type="submit" disabled={isLoading}>
            <i className={`fa-solid ${isLoading ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'} me-2`} />
            Tìm kiếm
          </button>
        </form>

        <div className="affiliate-tree-columns">
          <span>Tài khoản</span>
          <span>Mã và vai trò</span>
          <span>Quan hệ giới thiệu</span>
          <span>Tuyến dưới</span>
        </div>

        <div className="affiliate-tree-list">
          {isLoading ? (
            <div className="affiliate-tree-empty">
              <i className="fa-solid fa-spinner fa-spin" />
              <strong>Đang tải cây hệ thống</strong>
              <span>Vui lòng chờ trong giây lát.</span>
            </div>
          ) : roots.length === 0 ? (
            <div className="affiliate-tree-empty">
              <i className="fa-solid fa-sitemap" />
              <strong>Không tìm thấy mạng lưới phù hợp</strong>
              <span>Kiểm tra lại từ khóa hoặc thay đổi độ sâu hiển thị.</span>
            </div>
          ) : roots.map(root => (
            <AffiliateUserNode key={root.userId} node={root} />
          ))}
        </div>
      </section>
    </div>
  );
}

export default AffiliateTreeScreen;
